import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import {
  DeviceDataWrapper,
  UpdateDeviceLogisticResponse,
} from '../shared/models/device.model';
import { DeviceService } from '../shared/services/device.service';
import {
  DetailOpenMode,
  DeviceTypeKeys,
} from '../shared/models/device-types-attributes';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  DeviceListItem,
  getCustomerName,
  getDeviceId,
  getExternalDeviceId,
} from '../shared/models/device-list.model';
import { MatTab } from '@angular/material/tabs';
import { DeviceOpstateItemsComponent } from './device-opstate-items/device-opstate-items.component';

@Component({
  selector: 'app-device-detail',
  templateUrl: './device-detail.component.html',
  styleUrls: ['./device-detail.component.scss'],
})
export class DeviceDetailComponent implements OnInit {
  private devicesByTypeCode: Record<
    DeviceTypeKeys,
    DeviceDataWrapper | undefined
  > = {
    [DeviceTypeKeys.NONE]: undefined,
    [DeviceTypeKeys.SZT]: undefined,
    [DeviceTypeKeys.GATE]: undefined,
    [DeviceTypeKeys.SIMCARD]: undefined,
  };

  protected itemData: DeviceListItem;

  @ViewChild('opstateTab')
  private opstateTab: MatTab | undefined;

  @ViewChild('opstateItems')
  private opstateItems: DeviceOpstateItemsComponent | undefined;

  protected get data(): DeviceDataWrapper | undefined {
    return this.devicesByTypeCode[this.itemData.externalDeviceTypeCode];
  }

  protected get deviceLoaded(): boolean {
    return Boolean(
      this.data?.device &&
        this.data.deviceTypeData &&
        this.data.deviceParameters
    );
  }

  public itemEditEventEmitter =
    new EventEmitter<UpdateDeviceLogisticResponse>();

  constructor(
    private deviceService: DeviceService,
    private sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<DeviceDetailComponent>,
    @Inject(MAT_DIALOG_DATA)
    private dialogData: { itemData: DeviceListItem; openMode: DetailOpenMode },
    public dialog: MatDialog,
    private breakpointObserver: BreakpointObserver
  ) {
    this.itemData = dialogData.itemData;
  }

  private loadDetails(refresh = false) {
    if (this.deviceType === DeviceTypeKeys.NONE || (!refresh && this.data)) {
      return;
    }
    const opstateSelectedName = this.opstateItems?.selected?.nameDisplayText;
    this.devicesByTypeCode[this.itemData.externalDeviceTypeCode] = {};
    this.deviceService
      .getDeviceByExternalDeviceId(this.externalDeviceId)
      .subscribe((data) => {
        const { device, deviceTypeData } = data;
        this.data!.device = device;
        this.data!.deviceTypeData = deviceTypeData;
        this.deviceService
          .getDeviceSensitiveDataStatusByExternalDeviceId(
            device.externalDeviceId,
            device.externalDeviceTypeCode
          )
          .subscribe((hasSensitiveData) => {
            this.data!.hasSensitiveData = hasSensitiveData.valueOf();
          });
        if (this.dialogData.openMode === DetailOpenMode.MONITORING) {
          setTimeout(() => {
            this.navigateToMonitoring();
            this.dialogData.openMode = DetailOpenMode.REGULAR;
          }, 700);
        }
        if (refresh) {
          setTimeout(() => {
            if (this.opstateItems && opstateSelectedName) {
              this.opstateItems.selectDeviceOperationStateItem(
                opstateSelectedName
              );
            }
          });
        }
      });
    this.deviceService
      .getDeviceParametersByDeviceId(this.deviceId)
      .subscribe((data) => {
        this.data!.deviceParameters = data.deviceParameters ?? [];
      });
  }

  protected navigateToMonitoring() {
    this.opstateItems?.selectDeviceOperationStateItem('MONITORING_STATUS');
    this.tabIndex = this.opstateTab?.position ?? 0;
  }

  ngOnInit(): void {
    if (this.dialogData.openMode === DetailOpenMode.PLOTS) {
      this.loadPlot();
    }
    this.loadDetails();
  }

  protected changeDeviceWithType(type: DeviceTypeKeys) {
    this.tabIndex = 0;
    this.itemData.externalDeviceTypeCode = type;
    this.dialogData.openMode = DetailOpenMode.REGULAR;
    if (!this.data) {
      this.loadDetails();
    }
  }

  protected refresh() {
    if (this.dialogData.openMode === DetailOpenMode.PLOTS) {
      this.loadPlot();
    }
    this.loadDetails(true);
  }

  protected readonly Keys = DeviceTypeKeys;

  protected plotUrl: SafeUrl | undefined;

  protected loadPlot(): void {
    const url = `${environment.plotsRootPath}/${this.deviceId}_${this.externalDeviceId}/${this.deviceId}_${this.externalDeviceId}.html`;
    this.plotUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      url + '?' + new Date().getTime()
    );
  }

  protected clearPlot(): void {
    this.plotUrl = undefined;
  }

  protected get isMobile(): boolean {
    return this.breakpointObserver.isMatched(Breakpoints.HandsetPortrait);
  }

  protected get deviceId(): number {
    return getDeviceId(this.itemData);
  }

  protected get externalDeviceId(): string {
    return getExternalDeviceId(this.itemData);
  }

  protected get customerName(): string {
    return getCustomerName(this.itemData);
  }

  protected get deviceType(): DeviceTypeKeys {
    return this.itemData.externalDeviceTypeCode;
  }

  protected get bfAccessible(): boolean {
    return Boolean(this.itemData.sztExtDeviceId);
  }

  protected get editable(): boolean {
    return Boolean(this.data?.device?.writeRight);
  }

  protected tabIndex = 0;
}
